import { useState } from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { useMediaQuery } from "@/hooks/use-media-query"
import { Button } from "@/components/ui/button"
import { CalendarRange } from "@/components/ui/calendar-range"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DateRangeProps {
  className?: string
  date: DateRange | undefined
  setDate: React.Dispatch<React.SetStateAction<DateRange>>
}

export function DateRangePicker({ className, date, setDate }: DateRangeProps) {
  const [tempDate, setTempDate] = useState<DateRange | undefined>(date)
  const [open, setOpen] = useState(false)
  const isDesktop = useMediaQuery("(min-width: 768px)")

  const handleConfirm = (range: DateRange | undefined) => {
    setDate(range!)
    setOpen(false)
  }

  const handleCancel = () => {
    setTempDate(date)
    setOpen(false)
  }

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                `${format(date.from, "LLL dd, yyyy")} - ${format(date.to, "LLL dd, yyyy")}`
              ) : (
                format(date.from, "LLL dd, yyyy")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="z-50 w-auto p-0" align="center">
          <CalendarRange
            range={tempDate}
            onRangeChange={setTempDate}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            numberOfMonths={isDesktop ? 2 : 1}
            classNames={{
              day_selected: "bg-teal-600 text-white font-semibold",
              day_range_start:
                "bg-teal-600 text-white font-semibold rounded-l-full",
              day_range_end:
                "bg-teal-600 text-white font-semibold rounded-r-full",
              day_range_middle: "bg-teal-100 text-teal-900",
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
